import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, FileText, Mail, MessageSquare } from 'lucide-react';
import { InvoiceProjectCustomer, InvoiceTotals, InvoiceToCustomerRequest, Invoice } from './invoiceType';
import { formatAddress, formatCurrency } from '../lib/global_function';
import InvoiceApi from './invoiceApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { getAccountIdFromSessionStorage } from '../lib/global_function'

const InvoiceToCustomerPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/invoice' },
    { label: 'Send Invoice', path: '/main/project/invoice/tocustomer' },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const invoiceProjectCustomer: InvoiceProjectCustomer | undefined = location.state?.invoice;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSentSuccessfully, setIsSentSuccessfully] = useState(false);


  // Check if invoice is available
  useEffect(() => {
    if (!invoiceProjectCustomer) {
      setErrorMessage('Invoice not available, please select an invoice by clicking Go To Invoice button.');
    }
  }, [invoiceProjectCustomer]);

const handleView = async () => {
  if (!invoiceProjectCustomer) {
    setErrorMessage('No invoice selected');
    return;
  }

  setErrorMessage(null);
  setIsLoading(true);

  try {
    const pdfBlob = await InvoiceApi.viewInvoicePdf(invoiceProjectCustomer.invoice_id);
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    
    // Optional: Clean up the object URL after opening
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    setErrorMessage(error instanceof Error ? error.message : 'Failed to generate PDF');
  } finally {
    setIsLoading(false);
  }
};

  const handleSend = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);
    setIsSentSuccessfully(false);

    if (isLoading) {
      return;
    }

    if (!invoiceProjectCustomer) {
      setErrorMessage('No invoice selected');
      setIsLoading(false);
      return;
    }

    if (!sendMethod) {
      setErrorMessage('Please select a send method');
      setIsLoading(false);
      return;
    }

    const accountId = getAccountIdFromSessionStorage((process.env.REACT_APP_SESSION_STORAGE_KEY as string));
    if (!accountId) {
      setErrorMessage('Account not available.');
      setIsLoading(false);
      return;
    }

    try {
      const requestBody: InvoiceToCustomerRequest = {
        invoice_id: invoiceProjectCustomer.invoice_id,
        account_id: accountId,
        project_id: invoiceProjectCustomer.project_id,
        customer_id: invoiceProjectCustomer.customer_id,
        send_method: sendMethod === 'text' ? 'sms' : sendMethod,
        recipient_email: invoiceProjectCustomer.customer_email,
        recipient_phone: invoiceProjectCustomer.customer_phone,
        message_for: "invoice",
      };

      const response = await InvoiceApi.sendInvoiceToCustomer(requestBody);
      setIsSentSuccessfully(true);
      setSuccessMessage('Your invoice PDF has been generated and is now being sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send invoice');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToInvoiceList = () => {
    navigate('/main/project/invoice');
  };

  // Helper function to safely format address
  const safeFormatAddress = () => {
    if (!invoiceProjectCustomer) return '';
    return formatAddress(
      invoiceProjectCustomer.customer_address_1,
      invoiceProjectCustomer.customer_address_2,
      invoiceProjectCustomer.customer_city,
      invoiceProjectCustomer.customer_state,
      invoiceProjectCustomer.customer_zip
    );
  };

  const balanceDue = invoiceProjectCustomer 
    ? Number(invoiceProjectCustomer.final_total) - Number(invoiceProjectCustomer.amount_paid)
    : 0;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Send Invoice to Customer</h1>

              {errorMessage && invoiceProjectCustomer && (
                <Alert variant="error">
                  {errorMessage}
                </Alert>
              )}

              {/* Success Message */}
              {successMessage && (
                <Alert variant="success">
                  {successMessage}
                </Alert>
              )}
          {/* Error Message - When no invoice */}
          {errorMessage && !invoiceProjectCustomer && (
            <div className="mb-6">
              <Alert variant="error">
                {errorMessage}
              </Alert>
              <div className="mt-4">
                <Button size="medium" onClick={handleNavigateToInvoiceList}>
                  Go To Invoice
                </Button>
              </div>
            </div>
          )}

          {invoiceProjectCustomer ? (
            <div className="space-y-6">
              {/* Customer Information */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <h2 className="text-lg font-semibold text-foreground mb-4">Customer Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Name:</span>
                    <span className="ml-2 text-foreground">
                      {`${invoiceProjectCustomer.customer_first_name} ${invoiceProjectCustomer.customer_last_name}`}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Email:</span>
                    <span className="ml-2 text-foreground">{invoiceProjectCustomer.customer_email}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Phone:</span>
                    <span className="ml-2 text-foreground">{invoiceProjectCustomer.customer_phone}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Address:</span>
                    <span className="ml-2 text-foreground">{safeFormatAddress()}</span>
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Invoice Details</h2>
                  <Button
                    size="medium"
                    variant="inverted"
                    onClick={handleView}
                    disabled={isLoading}
                  >
                    <FileText size={18} />
                    View PDF
                  </Button>
                </div>

                <div className="mb-4">
                  <span className="font-semibold text-gray-700">Project Name:</span>
                  <span className="ml-2 text-foreground">{invoiceProjectCustomer.project_name}</span>
                </div>

                {/* Invoice Summary */}
                <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <h3 className="font-semibold text-foreground mb-3">Invoice Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Subtotal (Before Tax):</span>
                      <span className="text-foreground">
                        ${formatCurrency(Number(invoiceProjectCustomer.sub_total_sum_lineitems).toFixed(2))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Tax Rate:</span>
                      <span className="text-foreground">
                        {Number(invoiceProjectCustomer.tax_rate).toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Tax Amount:</span>
                      <span className="text-foreground">
                        ${formatCurrency(Number(invoiceProjectCustomer.tax_amount).toFixed(2))}
                      </span>
                    </div>
                    {invoiceProjectCustomer.discount_usd > 0 && (
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">Discount:</span>
                        <span className="text-red-600 font-semibold">
                          -${formatCurrency(Number(invoiceProjectCustomer.discount_usd).toFixed(2))}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-300">
                      <span className="font-bold text-foreground">Total Amount:</span>
                      <span className="font-bold text-primary">
                        ${formatCurrency(Number(invoiceProjectCustomer.final_total).toFixed(2))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Paid Amount:</span>
                      <span className="text-green-600 font-semibold">
                        -${formatCurrency(Number(invoiceProjectCustomer.amount_paid).toFixed(2))}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-gray-400">
                      <span className="font-bold text-foreground">Balance Due:</span>
                      <span className="font-bold text-primary">
                        ${formatCurrency(balanceDue.toFixed(2))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Send Options */}
              {!isSentSuccessfully && (
                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    Send Invoice to Customer
                  </h2>

                  {/* Informational text */}
                  {sendMethod && (
                    <Alert variant="info" className="mb-4">
                      {sendMethod === 'email' ? (
                        <p className="text-sm">
                          The invoice will be sent as a PDF file attachment in an email from MrJoe@MrJoe.pro. 
                          By clicking Send Email, you confirm that the customer{' '}
                          <strong>{invoiceProjectCustomer.customer_first_name} {invoiceProjectCustomer.customer_last_name}</strong>{' '}
                          has agreed to receive this email to review the invoice you provided.
                        </p>
                      ) : (
                        <p className="text-sm">
                          The invoice will be sent as a link in a text message from (800)579-5808. 
                          By clicking Send Text, you confirm that the customer{' '}
                          <strong>{invoiceProjectCustomer.customer_first_name} {invoiceProjectCustomer.customer_last_name}</strong>{' '}
                          has agreed to receive this message to review the invoice you provided.
                        </p>
                      )}
                    </Alert>
                  )}

                  {/* Radio buttons */}
                  <div className="space-y-3 mb-6">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Select delivery method:</p>
                    <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        value="email"
                        checked={sendMethod === 'email'}
                        onChange={() => setSendMethod('email')}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <Mail size={18} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">Email</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        value="text"
                        checked={sendMethod === 'text'}
                        onChange={() => setSendMethod('text')}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <MessageSquare size={18} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">Text Message (SMS)</span>
                    </label>
                  </div>

                  {/* Send button */}
                  <div>
                    <Button
                      size="medium"
                      onClick={handleSend}
                      disabled={isLoading || !sendMethod}
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" fullScreen={false} className="size-5" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          {sendMethod ? `Send ${sendMethod === 'email' ? 'Email' : 'Text'}` : 'Select Method'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}


            </div>
          ) : (
            !errorMessage && (
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <p className="text-gray-600">No invoice data available to display.</p>
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceToCustomerPage;